import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Typography from '@tiptap/extension-typography';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code2, Link as LinkIcon, Image as ImageIcon,
  Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, Table as TableIcon,
  Trash2, Eraser,
} from 'lucide-react';

import { uploadsApi } from './api';
import { sanitizePastedHtml, isProbablyMarkdown, markdownToHtml } from './pasteSanitizer';

/**
 * A proper WYSIWYG HTML editor (contentEditable, powered by TipTap / ProseMirror).
 * - Handles paste from Word, Google Docs, and LibreOffice/OpenOffice Writer.
 * - Produces clean HTML (with `sanitizePastedHtml` on every paste).
 * - Backwards compatible: if the incoming `value` looks like Markdown, it is
 *   converted to HTML at mount time so legacy articles keep rendering.
 */
export default function RichHtmlEditor({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  minHeight = 480,
  onUploadImage,
  testid,
}) {
  const initialHtml = useRef(
    isProbablyMarkdown(value || '') ? markdownToHtml(value || '') : (value || '')
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        codeBlock: { HTMLAttributes: { class: 'esi-code' } },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Typography,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    content: initialHtml.current,
    editorProps: {
      attributes: {
        class: 'rte-content prose-esi focus:outline-none',
        style: `min-height: ${minHeight}px;`,
        spellcheck: 'true',
      },
      // Clean Word / Google Docs / LibreOffice HTML BEFORE ProseMirror parses it.
      transformPastedHTML(html) {
        return sanitizePastedHtml(html);
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange && onChange(html === '<p></p>' ? '' : html);
    },
  });

  // If the caller resets `value` (e.g. switching language) push it in without
  // fighting the user's typing.
  useEffect(() => {
    if (!editor) return;
    const incoming = isProbablyMarkdown(value || '') ? markdownToHtml(value || '') : (value || '');
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = useCallback(async (file) => {
    if (!file || !editor) return;
    setUploading(true);
    try {
      let url;
      if (onUploadImage) {
        url = await onUploadImage(file);
      } else {
        const res = await uploadsApi.uploadImage(file);
        url = uploadsApi.publicUrl(res.url);
      }
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      alert('Image upload failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [editor, onUploadImage]);

  const promptLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  if (!editor) return null;

  return (
    <div
      className="rte-wrapper border border-slate-200 rounded-lg bg-white overflow-hidden"
      data-testid={testid}
    >
      <Toolbar
        editor={editor}
        onOpenImage={() => fileRef.current?.click()}
        onLink={promptLink}
        uploading={uploading}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleImageUpload(e.target.files?.[0])}
      />
      <div className="rte-scroll">
        <EditorContent editor={editor} />
      </div>
      <WordCountBar editor={editor} />
    </div>
  );
}

/* -------------------------- Word count meter ----------------------------- */

function WordCountBar({ editor }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const bump = () => setTick((t) => t + 1);
    editor.on('update', bump);
    return () => editor.off('update', bump);
  }, [editor]);

  const stats = useMemo(() => {
    if (!editor) return { words: 0, chars: 0, minutes: 0 };
    // eslint-disable-next-line no-unused-expressions
    tick;
    const text = editor.getText().replace(/\u00a0/g, ' ').trim();
    const words = text ? text.split(/\s+/).filter(Boolean).length : 0;
    const chars = text.length;
    const minutes = Math.max(1, Math.round(words / 220)); // ~220 wpm average
    return { words, chars, minutes };
  }, [editor, tick]);

  // SEO-friendly length guides. Green >=800, amber 300-799, grey <300.
  const tone =
    stats.words >= 800 ? 'text-emerald-700' :
    stats.words >= 300 ? 'text-amber-700' :
    'text-slate-500';
  const goal =
    stats.words >= 1500 ? 'Long-form ready' :
    stats.words >= 800 ? 'SEO-ready' :
    stats.words >= 300 ? 'Draft length' :
    'Keep going';

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-1.5 border-t border-slate-200 bg-slate-50/70 text-xs text-slate-500"
      data-testid="rte-word-count"
    >
      <span className={`font-semibold ${tone}`}>{stats.words.toLocaleString()} words</span>
      <span>{stats.chars.toLocaleString()} characters</span>
      <span>{stats.minutes} min read</span>
      <span className="ml-auto text-[11px] uppercase tracking-wider text-slate-400">
        {goal}
      </span>
    </div>
  );
}

/* ----------------------------- Toolbar ----------------------------------- */

const HBtn = ({ level, editor }) => (
  <button
    type="button"
    onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
    className={cls('rte-btn font-bold text-[13px]', editor.isActive('heading', { level }) && 'rte-btn-active')}
    title={`Heading ${level}`}
  >
    H{level}
  </button>
);

const IBtn = ({ icon: Icon, active, onClick, title, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cls('rte-btn', active && 'rte-btn-active', disabled && 'opacity-40 cursor-not-allowed')}
    title={title}
    aria-label={title}
  >
    <Icon size={15} />
  </button>
);

function Toolbar({ editor, onOpenImage, onLink, uploading }) {
  if (!editor) return null;
  return (
    <div className="rte-toolbar flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/70 sticky top-0 z-10">
      <button
        type="button"
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={cls('rte-btn text-[12px]', editor.isActive('paragraph') && !editor.isActive('heading') && 'rte-btn-active')}
        title="Paragraph"
      >P</button>
      <HBtn level={1} editor={editor} />
      <HBtn level={2} editor={editor} />
      <HBtn level={3} editor={editor} />

      <Divider />

      <IBtn icon={Bold}          active={editor.isActive('bold')}          onClick={() => editor.chain().focus().toggleBold().run()}          title="Bold (Ctrl+B)" />
      <IBtn icon={Italic}        active={editor.isActive('italic')}        onClick={() => editor.chain().focus().toggleItalic().run()}        title="Italic (Ctrl+I)" />
      <IBtn icon={UnderlineIcon} active={editor.isActive('underline')}     onClick={() => editor.chain().focus().toggleUnderline().run()}     title="Underline (Ctrl+U)" />
      <IBtn icon={Strikethrough} active={editor.isActive('strike')}        onClick={() => editor.chain().focus().toggleStrike().run()}        title="Strikethrough" />

      <Divider />

      <IBtn icon={List}          active={editor.isActive('bulletList')}    onClick={() => editor.chain().focus().toggleBulletList().run()}    title="Bulleted list" />
      <IBtn icon={ListOrdered}   active={editor.isActive('orderedList')}   onClick={() => editor.chain().focus().toggleOrderedList().run()}   title="Numbered list" />
      <IBtn icon={Quote}         active={editor.isActive('blockquote')}    onClick={() => editor.chain().focus().toggleBlockquote().run()}    title="Blockquote" />
      <IBtn icon={Code2}         active={editor.isActive('codeBlock')}     onClick={() => editor.chain().focus().toggleCodeBlock().run()}     title="Code block" />

      <Divider />

      <IBtn icon={AlignLeft}     active={editor.isActive({ textAlign: 'left' })}   onClick={() => editor.chain().focus().setTextAlign('left').run()}   title="Align left" />
      <IBtn icon={AlignCenter}   active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align center" />
      <IBtn icon={AlignRight}    active={editor.isActive({ textAlign: 'right' })}  onClick={() => editor.chain().focus().setTextAlign('right').run()}  title="Align right" />

      <Divider />

      <IBtn icon={LinkIcon}      active={editor.isActive('link')}          onClick={onLink}                                                   title="Insert / edit link" />
      <IBtn icon={ImageIcon}     onClick={onOpenImage}                                                                                        title={uploading ? 'Uploading...' : 'Insert image'} disabled={uploading} />
      <IBtn icon={TableIcon}     onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}          title="Insert table" />

      <Divider />

      <IBtn icon={Eraser}        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}                                    title="Clear formatting" />
      <IBtn icon={Trash2}        onClick={() => { if (window.confirm('Clear the whole article?')) editor.chain().focus().clearContent(true).run(); }} title="Clear everything" />
      <IBtn icon={Undo2}         onClick={() => editor.chain().focus().undo().run()}                                                          title="Undo (Ctrl+Z)" disabled={!editor.can().undo()} />
      <IBtn icon={Redo2}         onClick={() => editor.chain().focus().redo().run()}                                                          title="Redo (Ctrl+Shift+Z)" disabled={!editor.can().redo()} />
    </div>
  );
}

const Divider = () => <span className="w-px h-5 bg-slate-200 mx-1" />;
const cls = (...xs) => xs.filter(Boolean).join(' ');
